// api/companyApi.ts
import { baseApi } from "./BaseApi";
import {
  Company,
  CompanySummary,
  CreateCompanyReq,
  InviteUsersToCompanyReq,
  JobSummary,
  UpdateEmployeeReq,
} from "../types/companyTypes";
import { Employee } from "../types/commonTypes";
import { Job } from "../types/jobTypes";
import {
  AddEmployeeReq,
  EmployeeDetails,
  GetJobsSummaryResp,
} from "../types/companyTypes";
import { DateTime } from "luxon";

interface ApiJob extends Omit<Job, "startTime"> {
  startTime: string;
}

const companyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<Company[], void>({
      query: () => `/companies/user`,
    }),
    getCompany: builder.query<Company, number>({
      query: (arg) => `/companies/${arg}`,
    }),
    getCompanyEmployees: builder.query<Employee[], number>({
      query: (arg) => `/companies/${arg}/employees`,
      providesTags: ["CompanyEmployees"],
    }),
    getCompanyEmployee: builder.query<
      EmployeeDetails,
      { companyId: number; employeeId: string }
    >({
      query: ({ companyId, employeeId }) =>
        `/companies/${companyId}/employees/${employeeId}`,
    }),
    addCompanyEmployee: builder.mutation<
      Employee,
      { companyId: number; req: AddEmployeeReq }
    >({
      query: ({ companyId, req }) => ({
        url: `/companies/${companyId}/employees`,
        method: "POST",
        body: req,
      }),
      invalidatesTags: ["CompanyEmployees"],
    }),
    getCompanyJobsSummary: builder.query<GetJobsSummaryResp, number>({
      query: (arg) => `/companies/${arg}/jobs/summary`,
    }),
    getCompanySummary: builder.query<CompanySummary, number>({
      query: (arg) => `/companies/${arg}/summary`,
    }),
    getCompanyJobs: builder.query<Job[], number>({
      query: (arg) => `/companies/${arg}/jobs`,
      transformResponse: (response: ApiJob[]): Job[] => {
        return response.map((apiJob) => ({
          ...apiJob,
          startTime: DateTime.fromISO(apiJob.startTime, {
            zone: "utc",
          }).setZone("America/Los_Angeles"),
        }));
      },
    }),
    updateCompanyEmployee: builder.mutation<
      void,
      { companyId: number; employeeId: string; data: UpdateEmployeeReq }
    >({
      query: ({ companyId, employeeId, data }) => ({
        url: `/companies/${companyId}/employees/${employeeId}`,
        method: "PUT",
        body: data,
      }),
    }),
    inviteUsersToCompany: builder.mutation<void, InviteUsersToCompanyReq>({
      query: ({ companyId, users }) => ({
        url: `/companies/${companyId}/invite-users`,
        method: "POST",
        body: { users },
      }),
    }),

    emailJobDetailsToAgent: builder.mutation<
      void,
      { companyId: number; jobId: number }
    >({
      query: ({ companyId, jobId }) => ({
        url: `/companies/${companyId}/jobs/${jobId}/agents/email`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyEmployeesQuery,
  useGetCompanyQuery,
  useLazyGetCompanyQuery,
  useGetCompanyJobsQuery,
  useLazyGetCompanyJobsQuery,
  useLazyGetCompanyJobsSummaryQuery,
  useLazyGetCompanySummaryQuery,
  useInviteUsersToCompanyMutation,
  useLazyGetCompanyEmployeesQuery,
  useAddCompanyEmployeeMutation,
  useLazyGetCompanyEmployeeQuery,
  useUpdateCompanyEmployeeMutation,
  useEmailJobDetailsToAgentMutation,
} = companyApi;

export default companyApi;
